/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack, Tooltip, TooltipContent, TooltipTrigger } from '@landler/component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CLIMATE_RISK_FACT_TYPES, ClimateRiskRating, ClimateRiskRatingsByIndex } from '@/api/rest/resources/types/fact';

import { RadialGradient } from '../Charts/components/RadialGradient';
import { Icon } from '../widgets/a/Icon/Icon';

const MAX_RADIUS = 100;
const cx = 150;
const cy = 150;
const MAX_RISK_RATING = 5;

export const RiskSectorChart = ({ risks }: { risks: Record<string, any>[] }) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState({ x: 0, y: 0, content: '' });

  const noActiveRisks = risks.every((risk) => risk.value === 0);

  const onlyOne = risks.length === 1;
  const anglePerSector = onlyOne ? 2 * Math.PI : (2 * Math.PI) / risks.length;

  const baseSectors = risks.map((risk, index) => {
    const startAngle = onlyOne ? 0 : index * anglePerSector - Math.PI / 2;
    const endAngle = onlyOne ? 2 * Math.PI : startAngle + anglePerSector;
    const radius = MAX_RADIUS;

    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArcFlag = anglePerSector > Math.PI ? 1 : 0;

    let d = `
    M ${cx},${cy}
    L ${x1},${y1}
    A ${radius},${radius} 0 ${largeArcFlag} 1 ${x2},${y2}
    Z
  `;

    if (onlyOne) {
      d = `
      M ${cx} ${cy - radius}
      A ${radius} ${radius} 0 1 1 ${cx} ${cy + radius}
      A ${radius} ${radius} 0 1 1 ${cx} ${cy - radius}
      Z
    `;
    }

    return <path key={`base-${risk.name}`} d={d} fill='none' stroke='#EEEEEE' strokeWidth={1} />;
  });

  const sectors = risks.map((risk, index) => {
    const rawRadius = (risk.value / MAX_RISK_RATING) * MAX_RADIUS;
    const radius = rawRadius === 0 ? 1 : rawRadius;

    const fill = rawRadius === 0 ? 'transparent' : 'url(#riskGradient)';

    const startAngle = onlyOne ? 0 : index * anglePerSector - Math.PI / 2;
    const endAngle = onlyOne ? 2 * Math.PI : startAngle + anglePerSector;

    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);

    const largeArcFlag = anglePerSector > Math.PI ? 1 : 0;

    let d = `
      M ${cx},${cy}
      L ${x1},${y1}
      A ${radius},${radius} 0 ${largeArcFlag} 1 ${x2},${y2}
      Z
    `;

    if (onlyOne) {
      const path = `
        M ${cx} ${cy - radius}
        A ${radius} ${radius} 0 1 1 ${cx} ${cy + radius}
        A ${radius} ${radius} 0 1 1 ${cx} ${cy - radius}
        Z
      `;
      d = path;
    }

    const isDimmed = hoveredIndex !== null && hoveredIndex !== index;

    // TODO: MVP-CW lokalise
    const riskLevel = `${(ClimateRiskRatingsByIndex[risk?.value as number] ?? ClimateRiskRating.not_relevant).replace('_', ' ')} risk`;
    const riskName = t(`climateRisk.riskTypes.${risk?.name as CLIMATE_RISK_FACT_TYPES}.title`);
    const tooltipContent = t('climateRisk.aggregatedOverview.tooltip.body', {
      riskLevel,
      riskName,
    });
    return (
      <path
        key={risk.name}
        d={d}
        fill={fill}
        style={{ opacity: isDimmed ? 0.3 : 1, transition: 'opacity 0.2s ease' }}
        onMouseEnter={(e) => {
          setHoveredIndex(index);
          const rect = e.currentTarget.getBoundingClientRect();
          setTooltip({
            x: e.clientX - rect.left + 10,
            y: e.clientY - rect.top + 10,
            content: tooltipContent,
          });
        }}
        onMouseLeave={() => {
          setHoveredIndex(null);
          setTooltip({ ...tooltip, content: '' });
        }}
      />
    );
  });

  const labelWidth = 2 * MAX_RADIUS * Math.tan(anglePerSector / 2) * 0.9;
  const labelRadius = MAX_RADIUS + 40;

  const labels = risks.map((risk, index) => {
    const startAngle = onlyOne ? 0 : index * anglePerSector - Math.PI / 2;
    const endAngle = onlyOne ? 2 * Math.PI : startAngle + anglePerSector;
    const midAngle = (startAngle + endAngle) / 2;

    const x = cx + labelRadius * Math.cos(midAngle);
    const y = cy + labelRadius * Math.sin(midAngle);

    const boxX = x - labelWidth / 2 - 4;
    const boxY = y - 12;

    return (
      <foreignObject key={risk?.name} x={boxX} y={boxY} height={50} style={{ overflow: 'visible' }}>
        <div
          className='typography-caption w-fit whitespace-pre-wrap rounded-lg font-semibold text-text-primary p-1 bg-neutral-black-4 flex flex-row gap-1 items-center'
          style={{
            fontWeight: hoveredIndex === index ? 'bold' : 'semibold',
          }}
        >
          <Tooltip>
            <TooltipTrigger className='items-center'>
              <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
            </TooltipTrigger>
            <TooltipContent className='whitespace-pre-wrap'>
              <Stack>
                <span className='typography-body2Semibold'>
                  {t(`climateRisk.riskTypes.${risk?.name as CLIMATE_RISK_FACT_TYPES}.title`)}
                </span>
                {t(`climateRisk.riskTypes.${risk?.name as CLIMATE_RISK_FACT_TYPES}.tooltip.body`)}
              </Stack>
            </TooltipContent>
          </Tooltip>
          <span className='px-1'>{t(`climateRisk.riskTypes.${risk?.name as CLIMATE_RISK_FACT_TYPES}.title`)}</span>
        </div>
      </foreignObject>
    );
  });

  return (
    <>
      <span className='typography-overline font-medium text-text-primary flex flex-row items-baseline gap-1'>
        {noActiveRisks ? (
          <>
            <Icon icon='ri-error-warning-fill' className='text-error' />
            {t('climateRisk.noActiveRisks.title')}
            <Tooltip>
              <TooltipTrigger>
                <Icon icon='ri-information-line' className='text-active-54 typography-body1' />
              </TooltipTrigger>
              <TooltipContent className='whitespace-pre-wrap'>
                {t('climateRisk.noActiveRisks.tooltip.body')}
              </TooltipContent>
            </Tooltip>
          </>
        ) : null}
      </span>
      <Stack className='flex-col items-center gap-4'>
        <div
          className='flex justify-center items-center radius-2xl relative bg-white-100'
          style={{
            maxHeight: 300,
            width: '100%',
            maxWidth: 500,
            aspectRatio: 1,
          }}
        >
          {tooltip.content && (
            <div
              className='bg-white-100 absolute rounded-lg p-2 typography-caption elevation-1'
              style={{
                top: tooltip.y,
                left: tooltip.x,
                maxWidth: 200,
                pointerEvents: 'none',
              }}
            >
              <div>{tooltip.content}</div>
            </div>
          )}
          <svg viewBox='0 0 315 315' width='100%' height='100%' preserveAspectRatio='xMidYMid meet'>
            <defs>
              <RadialGradient
                id='riskGradient'
                cx='150'
                cy='150'
                r='100'
                gradientUnits='userSpaceOnUse'
                stops={[
                  { offset: '0%', stopColor: '#FFDD5F', stopOpacity: 1 },
                  { offset: '50%', stopColor: '#FFAA1E', stopOpacity: 1 },
                  { offset: '100%', stopColor: '#D83B09', stopOpacity: 1 },
                ]}
              />
            </defs>
            {baseSectors}
            {sectors}
            {labels}
          </svg>
        </div>
      </Stack>
    </>
  );
};
